<template>
  <div 
    ref="callFlowCanvas"
    class="cf-canvas"
    :class="{'cf-canvas--node-select-mode': goToNodeSelectMode}"
    @mousemove="handleMove"
    @touchmove="handleMove"
    @mousedown="handleDown"
    @touchstart="handleDown"
    @mouseup="handleUp"
    @touchend="handleUp"
    @mousewheel="handleWheel"
    @contextmenu.stop.prevent="showContextMenu($event)"
    @click="closeContextMenu"
  >
    <div
      v-if="isCallFlowLoading"
      class="cf-canvas__loader"
    >
      <Loader />
    </div>
    <div
      v-if="goToNodeSelectMode"
      class="cf-canvas__message"
    >
      {{ $t('NODE_SELECTION') }}
      <button
        class="cf-node__action m-l-10"
        @click="onGoToNodeSelectModeChange"
      >
        <i
          class="fa fa-times"
          aria-hidden="true"
        />
      </button>
    </div>

    <CallFlowLinks
      :links="links"
      :temp-link="tempLink"
      :nodes="nodes"
      :container="containerCopy"
      :node-link-details="nodeLinkDetailsCopy"
      @editNodeLinks="selectLinkOriginNode"
    />

    <Node
      v-for="node in nodes"
      :key="node.id"
      :node.sync="node"
      :options="optionsForChild"
      :has-child="node.hasChild"
      :node-link-details="nodeLinkDetailsCopy"
      :enable-dropzone="linking"
      :new-added="node.newAdded"
      @drag="nodeDrag"
      @showContextMenu="showContextMenu"
      @startConnection="startConnection(node, $event)"
      @endConnection="endConnection(node, $event)"
      @selectNode="selectNode"
      @duplicate="nodeDuplicate(node)"
      @removeConnection="removeConnection"
      @deleteNode="confirmDeleteNode"
      @update-scene-container="handleSceneContainerUpdate"
    />

    <!-- Sidebar form -->
    <template v-show="selectedNode">
      <NodeProperties
        ref="nodeProperties"
        :scene.sync="scene"
        :node="selectedNode"
        :route-to-node="routeToNode"
        :select-mode-active="goToNodeSelectMode"
        @deleteNode="confirmDeleteNode"
        @closeNodeProperties="closeNodeProperties"
        @duplicateNode="onDuplicateNode"
        @goToNodeSelectMode="onGoToNodeSelectModeChange"
        @selectNode="selectNode"
        @removeConnection="removeConnection"
        @showContextMenu="showContextMenu"
      />
    </template>

    <!-- Context menu -->
    <ul 
      v-show="contextMenu.isActive"
      ref="cfContextMenu"
      class="cf-context-menu"
      :style="contextMenuStyle"
    >
      <li
        v-for="(node, index) in nodesAvailableToAdd"
        :key="index"
        @click.prevent="drawNode(node)"
      >
        {{ node.displayName }}
      </li>
    </ul>

    <div class="cf-zoom">
      <button @click="zoomCanvas('+')">
        +
      </button>
      <button @click="zoomCanvas('-')">
        -
      </button>
    </div>


    <ConfirmationModal
      v-if="showConfirmation"
      :title="$t('DELETE_CALL_FLOW_NODE_CONFIRMATION_TITLE')"
      :show-confirm-button="true"
      :show-header-close-button="true"
      :show-custom-button="false"
      @close="showConfirmation = false"
      @confirm="deleteNode"
    />
  </div>
</template>

<script>
import { merge, cloneDeep } from 'lodash';
import messages from '../../locales';
import mouseHelper from '../../helpers/mouse';
import { getConnectionPos, getPossibleLinkFilter } from '../../helpers/callFlow';
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import { STATUS_ERROR, STATUS_PENDING } from '@/store/helpers';

import Node from './Node';
import CallFlowLinks from './CallFlowLinks';
import NodeProperties from './NodeProperties';
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal';
import Loader from '@/core/components/Loader';

export default {
  name: 'CallFlowContainer',
  components: {
    Node,
    NodeProperties,
    ConfirmationModal,
    CallFlowLinks,
    Loader,
  },
  props: {
    availableNodes: {
      type: Array,
      default() {
        return [];
      },
    },
    options: {
      type: Object,
      default() {
        return {};
      }
    },
  },
  data() {
    return {
      draggingNode: null,
      dragging: false,
      hasDragged: false,
      tempLink: null,
      contextMenu: {
        isActive: false,
        top: 0,
        left: 0,
        nodeX: 0,
        nodeY: 0
      },
      showConfirmation: false,
      routeToNode: null,
      nodeToDelete: null,
      goToNodeSelectMode: false,

      screenCenterX: 0,
      screenCenterY: 0,

      /**
       * @type {Object.<string, import('../../ivr').NodeLinkDetails>}
       */
      nodeLinkDetailsCopy: {},

      /** @type {import('../../ivr').SceneContainer} */
      containerCopy: {},
      linking: false,
    };
  },
  computed: {
    ...mapState('callFlows', [
      'callFlowTree',
    ]),

    ...mapState('callFlows', {
      links: state => state.scene.links,
      nodes: state => state.scene.nodes,
      container: state => state.scene.container,
      selectedNode: state => state.scene.selectedNode,

      /**
       * @type {import('vue').ComputedOptions<Object.<string, import('../../ivr').NodeLinkDetails>>}
       */
      nodeLinkDetails: state => state.scene.nodeLinkDetails
    }),

    ...mapGetters('callFlows', [
      'scene',
      'getCallFlowTreeStatus'
    ]),

    nodesAvailableToAdd() {
      return this.availableNodes
        .filter(n => n.id !== 'root')
        .sort((a, b) => {
          if (a.displayName < b.displayName) {
            return -1;
          }
          if (a.displayName > b.displayName) {
            return 1;
          }
          return 0;
        });
    },

    /**
     * @type {import('vue').ComputedOptions<boolean>}
     */
    isCallFlowLoading() {
      return this.getCallFlowTreeStatus.status == STATUS_PENDING;
    },

    optionsForChild() {
      return {
        scale: this.scale,
        center: {
          x: this.centerX,
          y: this.centerY,
        },
      };
    },

    /**
     * @type {import('vue').ComputedOptions<number>}
     */
    centerX: {
      get() {
        return this.containerCopy?.centerX;
      },

      set(value) {
        this.containerCopy = {
          ...this.containerCopy,
          centerX: value
        };
      }
    },

    /**
     * @type {import('vue').ComputedOptions<number>}
     */
    centerY: {
      get() {
        return this.containerCopy?.centerY;
      },
      
      set(value) {
        this.containerCopy = {
          ...this.containerCopy,
          centerY: value
        };
      }
    },

    /**
     * @type {import('vue').ComputedOptions<number>}
     */
    scale: {
      get() {
        return this.containerCopy?.scale || 0;
      },
      
      set(value) {
        this.containerCopy = {
          ...this.containerCopy,
          scale: value
        };
        
        this.setContainer(this.containerCopy);
      }
    },

    contextMenuStyle() {
      return { top: this.contextMenu.top + 'px', left: this.contextMenu.left + 'px' };
    }
  },

  watch: {
    /**
     * @param {Object.<string, import('../../ivr').NodeLinkDetails>} newValue
     */
    nodeLinkDetails(newValue) {
      this.nodeLinkDetailsCopy = cloneDeep(newValue);
    },

    container: {
      immediate: true,
      handler(newValue) {
        this.containerCopy = cloneDeep(newValue);
        this.containerCopy.centerY = this.containerCopy.centerY || this.screenCenterY;
        this.containerCopy.centerX = this.containerCopy.centerX || this.screenCenterX;
      }
    }
  },

  created() {
    this.newNodeOriginDetails = undefined;
    this.mouseX = 0;
    this.mouseY = 0;

    this.lastMouseX = 0;
    this.lastMouseY = 0;

    this.minScale = 0.2;
    this.maxScale = 3;

    this.linking = false;
    this.linkStartData = null;
  },
  
  mounted() {
    this.screenCenterX = this.$el.clientWidth / 6;
    this.screenCenterY = this.$el.clientHeight / 2.5;
    this.importScene();
  },

  methods: {
    ...mapActions('callFlows', [
      'removeNode',
      'removeLink',
      'addLink',
      'setSelectedNode',
      'updateNodePosition',
    ]),

    ...mapMutations('callFlows', [
      'setContainer',
      'toggleNodeSelection'
    ]),

    ...mapMutations('callFlows', {
      mutateSelectedState: 'setSelectedNode'
    }),

    handleMove(e) {
      // Check for touch event to be able to get coordinates
      let ePageX, ePageY, eClientX, eClientY, eCoordsObj;
      if (e.type.includes('touch')) {
        ePageX = e.touches[0].pageX;
        ePageY = e.touches[0].pageY;
        eClientX = e.touches[0].clientX;
        eClientY = e.touches[0].clientY;
        eCoordsObj = e.touches[0];
      } else {
        ePageX = e.pageX;
        ePageY = e.pageY;
        eClientX = e.clientX;
        eClientY = e.clientY;
        eCoordsObj = e;
      }

      // Check if dragging a node and follow movement
      if (this.draggingNode) {

        this.dragging = true;

        this.mouseX = ePageX || eClientX + document.documentElement.scrollLeft;
        this.mouseY = ePageY || eClientY + document.documentElement.scrollTop;

        if (this.dragging && !this.linking) {
          const diffX = this.mouseX - this.lastMouseX;
          const diffY = this.mouseY - this.lastMouseY;

          this.lastMouseX = this.mouseX;
          this.lastMouseY = this.mouseY;

          this.draggingNode.x = this.draggingNode.x + diffX / this.scale;
          this.draggingNode.y = this.draggingNode.y + diffY / this.scale;

          this.nodeLinkDetailsCopy[this.draggingNode.id].x = this.draggingNode.x;
          this.nodeLinkDetailsCopy[this.draggingNode.id].y = this.draggingNode.y;

          this.hasDragged = true;
        }
        return;
      }
      // Drag canvas position
      const mouse = mouseHelper.getMousePosition(this.$el, eCoordsObj);
      this.mouseX = mouse.x;
      this.mouseY = mouse.y;

      if (this.dragging) {
        const diffX = this.mouseX - this.lastMouseX;
        const diffY = this.mouseY - this.lastMouseY;

        this.lastMouseX = this.mouseX;
        this.lastMouseY = this.mouseY;

        this.centerX += diffX;
        this.centerY += diffY;

        this.hasDragged = true;
      }

      if (this.linking && this.linkStartData) {
        const linkStartPos = getConnectionPos(
          this.linkStartData.node,
          this.linkStartData.slotNumber,
          false,
          this.containerCopy
        );
        this.tempLink = {
          x1: linkStartPos.x,
          y1: linkStartPos.y,
          x2: this.mouseX,
          y2: this.mouseY,
        };
      }
    },
    
    handleDown(e) {
      // Check for touch event to be able to get coordinates
      let eCoordsObj, eWhich;
      if (e.type.includes('touch')) {
        eWhich = 0;
        eCoordsObj = e.touches[0];
      } else {
        eWhich = 1;
        eCoordsObj = e;
      }
      const target = e.target || e.srcElement;
      if ((target === this.$el || target.matches('svg, svg *')) && e.which === eWhich) {
        this.dragging = true;

        const mouse = mouseHelper.getMousePosition(this.$el, eCoordsObj);
        this.mouseX = mouse.x;
        this.mouseY = mouse.y;

        this.lastMouseX = this.mouseX;
        this.lastMouseY = this.mouseY;

        if (e.preventDefault) e.preventDefault();
      }
    },
    handleUp() {
      if (this.linking) {
        this.linking = false;
        this.tempLink = null;
        this.linkStartData = null;
      }

      if (this.dragging) {

        if (this.draggingNode) {
          this.updateNodePosition({ nodeId: this.draggingNode.id, x: this.draggingNode.x, y: this.draggingNode.y });
        }

        this.dragging = false;

        if (this.hasDragged) {
          this.setContainer(this.containerCopy);
          this.hasDragged = false;
        }
      }

      this.draggingNode = null;
    },
    handleWheel(e) {
      const target = e.target || e.srcElement;
      if ((target === this.$el || target.matches('svg, svg *'))) {
        if (e.preventDefault) e.preventDefault();

        const deltaScale = Math.pow(1.1, e.deltaY * -0.01);
        this.scale *= deltaScale;

        if (this.scale < this.minScale) {
          this.scale = this.minScale;
          return;
        } else if (this.scale > this.maxScale) {
          this.scale = this.maxScale;
          return;
        }

        const zoomingCenter = {
          x: this.mouseX,
          y: this.mouseY,
        };

        const deltaOffsetX = (zoomingCenter.x - this.centerX) * (deltaScale - 1);
        const deltaOffsetY = (zoomingCenter.y - this.centerY) * (deltaScale - 1);

        this.centerX -= deltaOffsetX;
        this.centerY -= deltaOffsetY;

        this.setContainer(this.containerCopy);
      }
    },
    showContextMenu(e, { node, slotNumber, isOnFailure, criteria, filter } = {}) {
      this.contextMenu.isActive = true;
      const mouse = mouseHelper.getMousePosition(this.$el, e);

      if (e.y + this.$refs.cfContextMenu.clientHeight > this.$el.clientHeight) {
        this.contextMenu.top = (e.y - this.$refs.cfContextMenu.clientHeight);
      } else {
        this.contextMenu.top = e.y;
      }

      if (e.x + this.$refs.cfContextMenu.clientWidth > this.$el.clientWidth) {
        this.contextMenu.left = (e.x - this.$refs.cfContextMenu.clientWidth);
      } else {
        this.contextMenu.left = e.x;
      }
      
      this.contextMenu.nodeY = mouse.y;
      this.contextMenu.nodeX = mouse.x;

      if (node) {
        this.newNodeOriginDetails = {
          originNode: this.nodeLinkDetails[node.id],
          node,
          slotNumber,
          targetSlotNumber: 0,
          isOnFailure,
          criteria,
          filter
        };
      }
    },
    closeContextMenu() {
      this.contextMenu.isActive = false;
      this.newNodeOriginDetails = undefined;
    },

    drawNode(node) {
      const coords = {
        x: (this.contextMenu.nodeX / this.scale) - (this.centerX / this.scale),
        y: (this.contextMenu.nodeY / this.scale) - (this.centerY / this.scale)
      };

      const thisNode = merge(node, coords);

      this.$emit('drawNode', thisNode, this.newNodeOriginDetails, false);

      this.newNodeOriginDetails = undefined;
    },

    // Connections
    startConnection(node, { slotNumber, isOnFailure, criteria, e }) {
      const alreadyLinked = this.links.find(
        (link) => link.originNode.id == node.id && link.originSlot == slotNumber
      );


      const possibleFilterValue = getPossibleLinkFilter(node, this.scene);

      if (!alreadyLinked || ((node.nodeTypeId == 'MenuNode' || node.nodeTypeId == 'SplitTestNode') && slotNumber !== 1 && possibleFilterValue != null) || node.nodeTypeId == 'RouterNode') {
        this.linkStartData = {
          node: this.nodeLinkDetails[node.id],
          slotNumber: slotNumber,
          isOnFailure,
          criteria
        };
        const linkStartPos = getConnectionPos(
          this.linkStartData.node,
          this.linkStartData.slotNumber,
          false,
          this.containerCopy,
        );
        this.tempLink = {
          x1: linkStartPos.x,
          y1: linkStartPos.y,
          x2: this.mouseX,
          y2: this.mouseY,
          isOnFailure,
        };
        const filter = node.nodeTypeId == 'SplitTestNode' ? 'Route: ' + possibleFilterValue : possibleFilterValue;
        if ((node.nodeTypeId == 'MenuNode' || node.nodeTypeId == 'SplitTestNode') && slotNumber !== 1) {
          this.possibleFilterValue = possibleFilterValue;
          this.tempLink.originNode = node;
          this.tempLink.filter = filter;
          this.linkStartData.filter = filter;
          this.linkStartData.node = node;
          this.linkStartData.weight = node.nodeTypeId == 'SplitTestNode' ? 1 : null;
        }
        this.linking = true;
        
      }
    },
    endConnection(targetNode, slotNumber) {
      this.addLink({ targetNode, linkStartData: this.linkStartData, slotNumber });

      this.linking = false;
      this.tempLink = null;
      this.linkStartData = null;
    },
    
    removeConnection(targetID) {
      const link = this.links.find((link) => {
        return link.targetNode.id == targetID;
      });

      this.removeLink(link.id);
    },

    onGoToNodeSelectModeChange() {
      if (this.goToNodeSelectMode) {
        this.goToNodeSelectMode = false;
        this.routeToNode = null;
      } else {
        this.goToNodeSelectMode = true;
      }
    },

    selectLinkOriginNode({ node, link }) {
      this.selectNode(this.nodes.find(n => n.id === node.id), link);
    },

    // Events
    selectNode(node, link) {
      if (!this.goToNodeSelectMode) {
        if (link) {
          node.selectedLinkId = link.id;
        }
        this.setSelectedNode(node);
      } else {
        this.goToNodeSelectMode = false;
        this.routeToNode = node;
      }
    },
    
    confirmDeleteNode(node) {
      this.nodeToDelete = node;
      this.showConfirmation = true;
    },

    deleteNode() {
      this.removeNode(this.nodeToDelete);

      if (this.selectedNode) this.closeNodeProperties(this.selectedNode);
      
      this.nodeToDelete = null;
      this.showConfirmation = false;
    },
    nodeDuplicate() {
      // placeholder for duplicate node
    },
    nodeDrag(values) {
      // Node dragging initiated from Node
      this.draggingNode = cloneDeep(this.nodeLinkDetails[values.node.id]);
      this.lastMouseX = values.lastCoords.x;
      this.lastMouseY = values.lastCoords.y;
    },
    importScene() {
      if (this.scene && (this.availableNodes && this.availableNodes.length)) {
        if (this.selectedNode) {
          const node = this.nodes.find((b) => this.selectedNode.id === b.id);
          if (node) {
            node.selected = true;
          }
        }
      }
    },

    // Commenting this out for now, we're heading in the direction of constantly tracking changes within the components 
    // and providing users with an end all be all save/publish mechanism
    // ---
    // async updateNodeSettings(node) {
    //   const nodeTemplate = this.availableNodes.find((n) => {
    //     return n.nodeTypeId === node.nodeTypeId;
    //   });
      
    //   const payload = {
    //     tree: getTreeSaveObject(this.callFlowTree.id, this.scene),
    //     node: {
    //       childLinks: [],
    //       childLinksLookup: {},
    //       id: node.id,
    //       isValid: node.isValid,
    //       nodeTemplate: nodeTemplate,
    //       nodeTypeId: node.nodeTypeId,
    //       posX: node.x,
    //       posY: node.y,
    //       settings: node.settings,
    //       stepName: node.stepName
    //     }
    //   };
    //   try {
    //     const res = await this.updateNode(payload);
    //   } catch(e) {
    //     // Do we need to do something here?
    //   }
    // },
    // End comment

    closeNodeProperties(node) {
      if (node) {
        this.toggleNodeSelection(node.id);
      }

      this.mutateSelectedState(null);
      this.goToNodeSelectMode = false;
      this.routeToNode = null;
    },
    zoomCanvas(operator) {
      const step = 0.1;
      if (operator == '-') {
        this.scale = this.scale - step;
      } else if (operator == '+') {
        this.scale = this.scale + step;
      }
      if (this.scale < this.minScale) {
        this.scale = this.minScale;
        return;
      } else if (this.scale > this.maxScale) {
        this.scale = this.maxScale;
        return;
      }
    },
    onDuplicateNode(node) {
      const coords = {
        x: node.x + 220,
        y: node.y
      };

      const thisNode = merge(node, coords);

      this.$emit('drawNode', thisNode);
    },

    handleSceneContainerUpdate({ center }) {
      if (center.x) {
        this.centerX = center.x;
      }

      if (center.y) {
        this.centerY = center.y;
      }
    }
  },
  i18n: {
    messages,
    locale: 'en',
  },
};
</script>